import React, { Children, ComponentProps, PropsWithChildren } from 'react';
import { StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import {
  HorizontalScrollRail,
  HStack,
  theme,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategory = (props: ComponentProps<typeof HorizontalScrollRail>) => {
  const { children } = props;

  const { match, currentBreakpoint } = useResponsive();
  const containerSpacing = useContainerSpacing();
  const contentContainerStyles = useStyle(
    () => ({
      paddingHorizontal: containerSpacing,
      columnGap: theme.spacing['2'],
    }),
    [containerSpacing],
  );

  if (currentBreakpoint.isXS) {
    return (
      <HorizontalScrollRail
        outerOffset={0}
        showNavControls={false}
        gap={theme.spacing['2']}
        contentContainerStyle={contentContainerStyles}
      >
        {children}
      </HorizontalScrollRail>
    );
  }

  const childrenCount = Children.count(children);

  return (
    <HStack
      itemsPerRow={childrenCount > 1 ? match([2, 2, 3]) : 1}
      gap={match([theme.spacing['2'], theme.spacing['4'], theme.spacing['6']])}
      itemStyle={styles.itemStyle}
    >
      {children}
    </HStack>
  );
};

export const HomeFilterableCategory = (props: PropsWithChildren) => {
  const { children } = props;

  const { match, screenDimensions } = useResponsive();
  const containerStyle = match([styles.filterableCategoryXS, styles.filterableCategorySM]);

  const childrenCount = Children.count(children);
  const shouldHaveOneItemPerRow = childrenCount === 1 || screenDimensions.width < 350;

  return (
    <HStack
      itemsPerRow={shouldHaveOneItemPerRow ? 1 : match([2, 2, 3])}
      gap={match([theme.spacing['2'], theme.spacing['4'], theme.spacing['6']])}
      itemStyle={styles.itemStyle}
      style={containerStyle}
    >
      {children}
    </HStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  flexView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['2'],
  },
  filterableCategoryXS: {
    paddingVertical: theme.spacing['6'],
  },
  filterableCategorySM: {
    paddingVertical: theme.spacing['6'],
  },
  itemStyle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
